"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const faqs = [{
  question: "What types of catalysts does Catalyst Resource Group recover?",
  answer: "We specialize in recovering valuable metals from spent CO catalysts and other industrial catalysts used in refining and petrochemical processes. Our primary focus is on materials containing Platinum Group Metals (PGMs) such as platinum, palladium, and rhodium, which often retain significant value even after their operational life has ended."
}, {
  question: "How is the value of my spent catalyst determined?",
  answer: "Value is determined through a transparent, multi-step process. First, we perform an initial evaluation based on catalyst type and volume. Then, we coordinate precise sampling and send materials to independent, qualified laboratories for assay testing. These assays confirm the exact concentration of precious metals, which are then valued according to current market prices and agreed-upon refining terms."
}, {
  question: "What is the typical timeline for the recovery and settlement process?",
  answer: "The timeline can vary depending on the volume and complexity of the material. Generally, the process involves transportation, sampling, assay analysis, and refining. We strive for efficiency and will provide a clear estimated schedule during the initial evaluation phase, keeping you informed from pickup to final payment."
}, {
  question: "Do you handle the logistics and transportation of hazardous materials?",
  answer: "Yes. Catalyst Resource Group manages the entire logistics process. We work with experienced, compliant transportation partners to ensure your spent materials are moved safely and in accordance with all environmental and safety regulations, minimizing liability and administrative burden for your team."
}, {
  question: "Why is independent lab analysis important?",
  answer: "Independent analysis ensures complete transparency and trust. By using third-party qualified laboratories for assay testing, we provide an unbiased verification of the precious metal content in your spent catalyst. This guarantees that your settlement is based on accurate, verified data rather than estimates."
}, {
  question: "Is the recovery process environmentally compliant?",
  answer: "Absolutely. Environmental responsibility is a core part of our business. We utilize refining partners who adhere to strict regulatory standards. Recovering metals from spent catalysts reduces the need for primary mining, lowers greenhouse gas emissions, and ensures that hazardous waste is processed correctly rather than ending up in landfills."
}];
export default function CommonRecoveryQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="common-recovery-questions" className="bg-background py-20 lg:py-24">
      <div className="container mx-auto max-w-4xl px-6">
        <div ref={ref} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="mb-12 text-center">
            <h2 className="text-primary mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              Answers to common questions about catalyst recovery, value assessment, and our refining process.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-border mb-4 border-b last:mb-0 last:border-0" data-index={i}>
                <AccordionTrigger className="text-primary hover:text-primary/80 text-left text-lg font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-mono text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="mt-12 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
            <p className="text-muted-foreground font-medium">
              Have a question that isn&apos;t listed here?
            </p>
            <Button asChild variant="link" className="text-primary h-auto p-0 text-base font-bold underline-offset-4">
              <Link to="/TalkToAnExpertPage">Talk to an Expert</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}